import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Input, Field, Select } from '@itds/elements/index.js';

import { AgencyContext } from '@brokerage/contexts/AgencyContext';

const Premium_Threshold_Options = [
  // Special "Exact" option (0.01%)
  { value: 0.01, label: 'Exact Match' },
  // Generate options from 10% down to 0.25% in 0.25% decrements
  ...Array(40)
    .fill()
    .map((_, i) => {
      const value = 10 - i * 0.25;

      return {
        value,
        label: `${value.toFixed(2)}%`
      };
    })
];

const General = ({ mobile_app, updateAgencyMobileApp }) => {
  const { member_names } = useContext(AgencyContext);
  const [errors, setErrors] = useState([]);
  const [data, setData] = useState({
    guid: mobile_app.guid,
    is_deactivated: mobile_app.is_deactivated,
    is_annuity_quotes_enabled: mobile_app.is_annuity_quotes_enabled,
    is_life_quotes_enabled: mobile_app.is_life_quotes_enabled,
    is_ltc_quotes_enabled: mobile_app.is_ltc_quotes_enabled,
    is_health_analyzer_enabled: mobile_app.is_health_analyzer_enabled,
    name: mobile_app.name,
    member_id: mobile_app.member_id,
    onesignal_app_id: mobile_app.onesignal_app_id,
    onesignal_rest_api_key: mobile_app.onesignal_rest_api_key,
    enable_quote_by_premium: mobile_app?.enable_quote_by_premium ?? false,
    quote_premium_threshold: mobile_app?.quote_premium_threshold ?? 10.0,
    health_analyzer_token: mobile_app?.health_analyzer_token ?? '',
    crm: mobile_app.crm,
    notes: mobile_app.notes,
    is_archived: mobile_app.is_archived,
    vive_score_enabled: mobile_app.vive_score_enabled
  });


  const handleChange = (name, e) => {
    setData({ ...data, [name]: e.target.value });
  };

  const handleCheck = (name, e) => {
    setData({ ...data, [name]: e.target.checked });
  };

  const handleSave = () => {
    const errors = [];

    if (!data.name) {
      errors.push('name');
    }

    setErrors(errors);

    if (!errors.length) {
      updateAgencyMobileApp(data);
    }
  };

  const handleArchiveToggle = () => {
    setData({ ...data, is_archived: !data.is_archived });
    updateAgencyMobileApp({ is_archived: !data.is_archived });
  };

  return (
    <>
      <div style={{ marginBottom: 20, flexDirection: 'row', display: 'flex', gap: 10 }}>
        <Checkbox
          checked={data.is_deactivated}
          label='Deactivated'
          onChange={handleCheck.bind(null, 'is_deactivated')}
        />
        <Checkbox
          checked={data.is_annuity_quotes_enabled}
          label='Annuity'
          onChange={handleCheck.bind(null, 'is_annuity_quotes_enabled')}
        />
        <Checkbox
          checked={data.is_life_quotes_enabled}
          label='Life'
          onChange={handleCheck.bind(null, 'is_life_quotes_enabled')}
        />
        <Checkbox
          checked={data.is_ltc_quotes_enabled}
          label='LTC'
          onChange={handleCheck.bind(null, 'is_ltc_quotes_enabled')}
        />
        <Checkbox
          checked={data.is_health_analyzer_enabled}
          label='Health Analyzer'
          onChange={handleCheck.bind(null, 'is_health_analyzer_enabled')}
        />
        <Checkbox
          checked={data.vive_score_enabled}
          disabled={data.is_archived}
          label='Display Product Score'
          onChange={handleCheck.bind(null, 'vive_score_enabled')}
        />
      </div>

      <div style={{ marginTop: 10, width: '49%' }}>
        <Field label='Health Analyzer ApiToken'>
          <Input
            onChange={handleChange.bind(null, 'health_analyzer_token')}
            readOnly={data.is_archived}
            value={data.health_analyzer_token || ''}
          />
        </Field>
      </div>

      <Field label='Name'>
        <Input
          invalid={errors.includes('name')}
          onChange={handleChange.bind(null, 'name')}
          required={true}
          value={data.name || ''}
        />
      </Field>

      <Field label='Member'>
        <Select
          onChange={handleChange.bind(null, 'member_id')}
          options={member_names}
          placeholder='-- Select Member --'
          value={data.member_id}
        />
      </Field>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Field label='OneSignal App ID' style={{ width: '49%' }}>
          <Input
            onChange={handleChange.bind(null, 'onesignal_app_id')}
            value={data.onesignal_app_id || ''}
          />
        </Field>
        <Field label='OneSignal Rest API Key' style={{ width: '49%' }}>
          <Input
            onChange={handleChange.bind(null, 'onesignal_rest_api_key')}
            value={data.onesignal_rest_api_key || ''}
          />
        </Field>
      </div>

      <Field label='Quote Type'>
        <Checkbox
          checked={data.enable_quote_by_premium}
          label='Quote by Premium'
          onChange={handleCheck.bind(null, 'enable_quote_by_premium')}
        />
        {data.enable_quote_by_premium && (
          <div style={{ marginTop: '10px', width: '49%' }}>
            <Field label='Premium Threshold (%)'>
              <Select
                disabled={data.is_archived}
                onChange={handleChange.bind(null, 'quote_premium_threshold')}
                options={Premium_Threshold_Options}
                value={data.quote_premium_threshold}
              />
            </Field>
          </div>
        )}
      </Field>

      <Field label='Lead Flow CRM' style={{ width: '25%' }}>
        <Select
          onChange={handleChange.bind(null, 'crm')}
          options={[
            { value: 'ixn', label: 'IXN' },
            { value: 'insureio', label: 'Insureio' }
          ]}
          value={data.crm}
        />
      </Field>

      <Field label='Internal Notes'>
        <Input
          height={120}
          onChange={handleChange.bind(null, 'notes')}
          type='textarea'
          value={data.notes}
        />
      </Field>

      <div style={{ display: 'flex' }}>
        <Button color='RED' onClick={handleArchiveToggle.bind(null)} style={{ marginRight: '5px' }}>
          {data.is_archived ? 'Unarchive' : 'Archive'}
        </Button>
        {data.is_archived ? null : <Button onClick={handleSave.bind(null)}>Save Changes</Button>}
      </div>
    </>
  );
};

General.propTypes = {
  mobile_app: PropTypes.shape({
    guid: PropTypes.string,
    is_deactivated: PropTypes.bool,
    is_annuity_quotes_enabled: PropTypes.bool,
    is_life_quotes_enabled: PropTypes.bool,
    is_ltc_quotes_enabled: PropTypes.bool,
    is_health_analyzer_enabled: PropTypes.bool,
    health_analyzer_token: PropTypes.string,
    name: PropTypes.string,
    member_id: PropTypes.number,
    onesignal_app_id: PropTypes.string,
    onesignal_rest_api_key: PropTypes.string,
    enable_quote_by_premium: PropTypes.bool,
    quote_premium_threshold: PropTypes.number,
    crm: PropTypes.string,
    notes: PropTypes.string,
    is_archived: PropTypes.bool,
    vive_score_enabled: PropTypes.bool
  }),
  updateAgencyMobileApp: PropTypes.func
};

export default General;
